<template>
  <main class="vendors-page">
    <div class="vendors-page__body" ref="body">
      <div class="vendors-page__row">
        <h1 class="vendors-page__title">Поставщики</h1>
        <div class="vendors-page__buttons">
          <button
            class="vendors-page__buttons-export"
            type="button"
            v-bind:disabled="isAwaitDownloadRequest"
            v-on:click="exportToExcel">
            EXCEL
          </button>
          <button
            class="vendors-page__buttons-add"
            type="button"
            v-on:click="openFormVendorDialog">
            Добавить поставщика
          </button>
        </div>
      </div>
      <vendor-table
        v-bind:vendor-list="vendorList"
        v-bind:is-await-request="isAwaitRequest"
        v-bind:sort="sort"
        v-on:delete-vendor="onDeleteVendor"
        v-on:edit-vendor="fetchVendor"
        v-on:sort-vendor-list="fetchVendorList"
      />
      <the-pagination v-model="page" v-bind:page-count="pageCount" />
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        v-bind:label="vendorFormTitle"
        v-if="showFormVendorDialog"
        v-on:cancel="closeFormVendorDialog">
        <form-vendor
          v-bind:vendor-id="selectedVendorId"
          v-bind:initial-name="selectedVendor.name"
          v-bind:initial-address="selectedVendor.address"
          v-bind:initial-contact="selectedVendor.contact"
          v-bind:initial-email="selectedVendor.email"
          v-bind:initial-phone="selectedVendor.phone"
          v-bind:input-errors="vendorInputErrors"
          v-on:submit="onSubmitVendor"
          v-on:close="closeFormVendorDialog" />
      </confirm-side-dialog>
    </side-slide-transition>
  </main>
</template>

<script>
import ThePagination from '@/components/ThePagination.vue';
import VendorTable from '@/components/Warehouse/VendorTable.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import FormVendor from '@/components/Warehouse/FormVendor.vue';
import { Vendor } from '@/storage/gateways/VendorGateway';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';

/**
 * @description Сохранить файл.
 * @param {any}    file Файл.
 * @param {string} name Название файла.
 * @returns {void}
 */
function saveFile(file, name) {
  const obj = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = obj;
  link.target = '_blank';
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  link.remove();
}

/**
 * @description Страница списка поставщиков.
 * @type {Vue}
 */
export default {
  name: 'ClinicPanelWarehouseVendorList',
  components: {
    ConfirmSideDialog,
    FormVendor,
    SideSlideTransition,
    VendorTable,
    ThePagination,
  },
  computed: {
    /**
     * @description Заголовок формы поставщика.
     * @returns {string}
     */
    vendorFormTitle() {
      return this.selectedVendorId ? 'Редактировать поставщика' : 'Добавить поставщика';
    },
  },
  created() {
    this.fetchVendorList({ type: 'name', order: 'asc' });
  },
  data() {
    return {
      vendorList: [],
      isAwaitRequest: false,
      sort: {},
      page: 1,
      pageCount: 0,
      selectedVendorId: '',
      selectedVendor: new Vendor(),
      vendorInputErrors: {},
      showFormVendorDialog: false,
      isAwaitDownloadRequest: false,
    };
  },
  methods: {
    /**
     * @description Создать поставщика.
     * @param {VendorRequest} request
     */
    createVendor(request) {
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.closeFormVendorDialog();
        this.vendorInputErrors = {};
        this.fetchVendorList(this.sort);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.vendorInputErrors = error.allCauses();
      };
      this
        .$vendorGateway
        .create(request.toQuery())
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Закрыть диалоговое окно формы поставщика.
     * @returns {void}
     */
    closeFormVendorDialog() {
      this.selectedVendorId = '';
      this.selectedVendor = new Vendor();
      this.showFormVendorDialog = false;
    },
    /**
     * @description Редактировать поставщика.
     * @param {VendorRequest} request
     */
    editVendor(request) {
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.closeFormVendorDialog();
        this.vendorInputErrors = {};
        this.fetchVendorList(this.sort);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.vendorInputErrors = error.allCauses();
      };
      this
        .$vendorGateway
        .edit(request.getId(), request.toQuery())
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Выгрузить в Excel.
     * @returns {void}
     */
    exportToExcel() {
      const onFail = (error) => this.notifyFail(error.title);
      const onSuccess = (file) => saveFile(file, 'Список поставщиков.xls');
      this.isAwaitRequest = true;
      this
        .$vendorGateway
        .getAllBySheet()
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
    /**
     * @description Открыть диалоговое окно формы поставщика.
     * @returns {void}
     */
    openFormVendorDialog() {
      this.showFormVendorDialog = true;
      this.vendorInputErrors = {};
    },
    /**
     * @description Получить список поставщиков.
     * @param {Object} sort Сортировка.
     * @returns {Promise<void>}
     */
    async fetchVendorList(sort) {
      this.sort = sort;
      const onSuccess = ({ vendors, pageCount }) => {
        this.vendorList = vendors;
        this.pageCount = pageCount;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.isAwaitRequest = true;
      const either = await this.$vendorGateway.getAll(
        this.sort,
        this.page,
      );
      either.fold(onFail, onSuccess);
      this.isAwaitRequest = false;
    },
    /**
     * @description Получить поставщика.
     * @param {string} vendorId Идентификатор поставщика.
     */
    async fetchVendor(vendorId) {
      const onSuccess = ({ vendor }) => {
        this.selectedVendorId = vendorId;
        this.selectedVendor = vendor;
        this.openFormVendorDialog();
      };
      const onFail = (error) => {
        this.selectedVendorId = '';
        this.notifyFail(error.getTitle());
      };
      this
        .$vendorGateway
        .viewVendor(vendorId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать отправку запроса поставщика.
     * @param {VendorRequest} request
     */
    onSubmitVendor(request) {
      if (request.getId()) {
        this.editVendor(request);
      } else {
        this.createVendor(request);
      }
    },
    /**
     * @description Обработать событие удаления поставщика.
     * @param {number} id Идентификатор поставщика.
     * @returns {void}
     */
    async onDeleteVendor(id) {
      const dialog = await this
        .showRemoveDialog('Вы действительно хотите безвозвратно удалить поставщика?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.fetchVendorList(this.sort);
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$vendorGateway
        .delete(id)
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
  mixins: [
    notifierMixin,
    builtInDialogMixin,
  ],
};
</script>

<style lang="scss" scoped>
@use "@/scss/buttons";

.vendors {
  &-page {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    &__filters {
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 20px;
      &-label {
        color: gray;
        font-size: 12px;
      }
      &-additional {
        display: flex;
        gap:10px;
        margin-top: 20px;
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: repeat(2, auto);
      max-width: 410px;
      width: 100%;
      column-gap: 10px;
      align-items: center;
      &-filter {
        margin-top: 25px;
        display: flex;
        align-items: center;
        gap: 5px;
        max-width: 175px;
        width: 100%;
        color: #1F85C8;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        >img {
          width: 15px;
          height: 15px;
        }
      }
      &-add {
        grid-column: 2/3;
        @include buttons.btn-blue(100%, 48px, 16px);
      }

      &-export {
        @include buttons.btn-transparent(100%, 48px, 16px);
        max-width: 155px;
        &:before {
          content: "";
          background-image: url("@/assets/img/exel.svg");
          background-repeat: no-repeat;
          background-size: contain;
          width: 16px;
          height: 16px;
          margin: 0 8px 0 0;
        }
        &:disabled {
          color: var(--unfocus-color);
          border-color: var(--unfocus-color);
          background-color: transparent;
        }
      }

      &-delete {
        width: 100%;
        @include buttons.btn-blue(50%, 48px, 16px);
      }

      &-delete:disabled {
        background-color: var(--unfocus-color);
      }
    }

    &__body {
      padding: 38px 20px 48px 23px;
      width: 100%;
      overflow-y: scroll;
    }

    &__row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }

    &__search {
      max-width: 400px;
    }
  }
}

@media screen and (max-width: 960px) {
  .vendors {
    &-page {
      flex-direction: column;

      &__row {
        flex-direction: column;
        gap: 10px;
      }

      &__search {
        max-width: 100%;
      }
    }
  }
}

@media screen and (max-width: 640px) {
  .vendors {
    &-page {
      &__body {
        padding: 38px 10px 140px;
        width: 100%;
        overflow-y: scroll;
      }

      &__buttons {
        grid-template-columns: 155px 1fr;
        max-width: 100%;
        row-gap: 10px;
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .vendors-page {
    &__buttons {
      grid-template-columns: minmax(200px, 230px);
      justify-content: left;
      &-export {
        display: none;
      }
      &-add {
        grid-column: auto;
      }
    }
  }
}
</style>
